/* eslint-disable @typescript-eslint/no-explicit-any */
import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnDestroy,
	OnInit,
	computed,
	effect,
	inject,
	signal,
	ChangeDetectorRef,
	ViewChildren,
	QueryList,
	ElementRef
} from '@angular/core';
// import { HttpClient } from '@angular/common/http';
import { BigFormService, IndexedDbService, } from '@flexus/core';
import { DocumentRequirement } from '../company-selection/company-selection.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';

const MAX_IMAGE_BYTES = 950 * 1024; // 950kB
const MAX_IMAGE_DIMENSION = 1536; // Max width/height for resized images

export interface UploadCard {
	document: DocumentRequirement;
	file: File | null;
	base64: string | null;
	fileName: string | null;
	thumbnail: string | null;
	isDragOver: boolean;
}

@Component({
	selector: 'flx-supporting-document-upload',
	templateUrl: './supporting-document-upload.component.html',
	styleUrls: ['./supporting-document-upload.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SupportingDocumentUploadComponent implements OnInit, OnDestroy {
	// Dependencies
	private readonly bf = inject(BigFormService);
	readonly cdr = inject(ChangeDetectorRef);
	private readonly indexedDb = inject(IndexedDbService);
	private readonly ENABLE_IMAGE_RESIZE_DEBUG = false;
	private readonly destroy$ = new Subject<void>();
	private readonly deliveryVoucherDocument: DocumentRequirement = {
		type: 'delivery_voucher',
		label: 'Delivery Voucher',
		required: false,
		allowed_file_types: ['pdf', 'jpg', 'jpeg', 'png'],
		max_file_size_mb: 10,
		validation_rules: {}
	};

	// Inputs
	@Input() requiredDocuments: DocumentRequirement[] = [];
	@Input() analyseFilesWithAi = false;
	@Input() onAnalyzeImages: (() => void) | null = null;

	// Signals
	readonly uploadCards = signal<UploadCard[]>([]);
	readonly isLoading = signal<boolean>(false);
	readonly documentsFromForm = signal<DocumentRequirement[]>([]);
	readonly extractionComplete = signal<boolean>(false);

	// Debug preview (pre/post resize)
	debugResizeVisible = false;
	debugResizeOriginal: string | null = null;
	debugResizeResized: string | null = null;
	debugResizeFileName: string | null = null;
	debugResizeOriginalBytes: number | null = null;
	debugResizeResizedBytes: number | null = null;
	debugResizeOriginalWidth: number | null = null;
	debugResizeOriginalHeight: number | null = null;
	debugResizeResizedWidth: number | null = null;
	debugResizeResizedHeight: number | null = null;

	// Debug preview pan/zoom state
	debugOriginalZoom = 1.4;
	debugOriginalOffsetX = 0;
	debugOriginalOffsetY = 0;
	debugResizedZoom = 1.4;
	debugResizedOffsetX = 0;
	debugResizedOffsetY = 0;

	// ViewChildren for file inputs
	@ViewChildren('fileInput') fileInputs!: QueryList<ElementRef<HTMLInputElement>>;

	// Computed signals
	readonly allRequiredUploaded = computed(() => {
		return this.uploadCards()
			.filter(card => card.document.required)
			.every(card => card.file !== null);
	});

	readonly hasAnyDocuments = computed(() => {
		return this.uploadCards().some(card => card.file !== null);
	});

	// Effect: sync requiredDocuments from form signal changes
	private readonly documentsEffect = effect(() => {
		const formDocs = this.documentsFromForm();
		
		if (formDocs.length > 0) {
			const expectedDocs = this.withDeliveryVoucher(formDocs);
			// Check if we need to update - compare document types
			const currentTypes = this.uploadCards().map(c => c.document.type).sort();
			const newTypes = expectedDocs.map(d => d.type).sort();
			const typesMatch = currentTypes.length === newTypes.length && 
				currentTypes.every((type, i) => type === newTypes[i]);

			if (!typesMatch || this.uploadCards().length === 0) {
				// Documents changed or no cards yet, initialize
				this.initializeUploadCards(formDocs);
			}
		} else if (this.uploadCards().length > 0) {
			// Form has no documents but we have cards - clear them
			this.uploadCards.set([]);
			this.cdr.markForCheck();
		}
	}, { allowSignalWrites: true });

	ngOnInit(): void {
		// Function to check and update documents from form
		const checkAndUpdateDocuments = (): boolean => {
			const configControl = this.bf.bigForm.get('tenant_document_config');
			if (!configControl) {
				return false;
			}

			const config = configControl.value;
			
			if (config && config.required_documents && Array.isArray(config.required_documents) && config.required_documents.length > 0) {
				this.documentsFromForm.set(config.required_documents);
				this.cdr.markForCheck();
				return true;
			}
			return false;
		};

		// Check immediately - form value might already be set
		checkAndUpdateDocuments();

		// Ensure the form control exists (in case it doesn't)
		if (!this.bf.bigForm.get('tenant_document_config')) {
			this.bf.bigForm.addControl('tenant_document_config', new UntypedFormControl(null));
		}

		// Subscribe to form changes
		const configControl = this.bf.bigForm.get('tenant_document_config');
		if (configControl) {
			// Subscribe to value changes
			configControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
				if (value?.required_documents && Array.isArray(value.required_documents) && value.required_documents.length > 0) {
					this.documentsFromForm.set(value.required_documents);
					this.cdr.markForCheck();
				} else {
					// Clear if no documents
					this.documentsFromForm.set([]);
					this.uploadCards.set([]);
					this.cdr.markForCheck();
				}
			});
		}

		// Also try to initialize from @Input if provided
		if (this.requiredDocuments.length > 0) {
			this.initializeUploadCards(this.requiredDocuments);
		} else {
			// Poll the form value multiple times to catch timing issues
			// Check after short delays - more aggressive polling
			const pollInterval = setInterval(() => {
				if (checkAndUpdateDocuments()) {
					clearInterval(pollInterval);
				}
			}, 100);

			// Stop polling after 2 seconds
			setTimeout(() => {
				clearInterval(pollInterval);
				// Final check
				checkAndUpdateDocuments();
			}, 2000);
		}
	}

	onAnalyzeImagesClick(): void {
		if (this.onAnalyzeImages) {
			this.syncUploadedDocsToBigForm();
			this.onAnalyzeImages();
		}
	}

	ngOnDestroy(): void {
		// Sync final state of uploaded documents to bigForm before component destroys
		// This allows the serverCall to access the documents for extraction
		this.syncUploadedDocsToBigForm();
		
		this.destroy$.next();
		this.destroy$.complete();
	}

	private syncUploadedDocsToBigForm(): void {
		const uploadedDocs = this.uploadCards()
			.filter(card => card.base64 && card.file)
			.map(card => ({
				id: card.document.type,
				name: card.fileName,
				base64: card.base64!,
				purpose: card.document.type
			}));

		console.log('Syncing uploaded docs to bigForm:', uploadedDocs.length, 'documents');

		// Store in bigForm for extraction serverCall to access
		const supportingDocsControl = this.bf.bigForm.get('supporting_docs');
		if (supportingDocsControl) {
			supportingDocsControl.setValue(uploadedDocs);
		} else {
			this.bf.bigForm.addControl('supporting_docs', new UntypedFormControl(uploadedDocs));
		}
	}

	private initializeUploadCards(docs?: DocumentRequirement[]): void {
		const baseDocuments = docs || this.requiredDocuments || this.documentsFromForm();

		if (!baseDocuments || baseDocuments.length === 0) {
			this.uploadCards.set([]);
			this.cdr.markForCheck();
			return;
		}
		const documents = this.withDeliveryVoucher(baseDocuments);

		// Get current document types
		const currentDocTypes = documents.map(d => d.type).sort();
		
		// Clear stored documents that don't match current document types
		this.clearStoredDocumentsForTypes(currentDocTypes);

		const cards: UploadCard[] = documents.map(doc => ({
			document: doc,
			file: null,
			base64: null,
			fileName: null,
			thumbnail: null,
			isDragOver: false
		}));

		this.uploadCards.set(cards);
		// Load stored documents after initializing cards (only for current types)
		this.loadStoredDocuments();
		this.cdr.markForCheck();
	}

	private withDeliveryVoucher(documents: DocumentRequirement[]): DocumentRequirement[] {
		if (!documents || documents.length === 0) {
			return documents;
		}
		const hasVoucher = documents.some(doc => doc.type === this.deliveryVoucherDocument.type);
		if (hasVoucher) {
			return documents;
		}
		return [...documents, this.deliveryVoucherDocument];
	}
	
	private async clearStoredDocumentsForTypes(currentTypes: string[]): Promise<void> {
		try {
			// Check if clearAllSupportingDocuments was recently called (within last 5 seconds)
			// This indicates we're starting fresh and should clear ALL documents, not preserve any
			const clearFlag = localStorage.getItem('_pgg_clear_all_docs_flag');
			const shouldClearAll = clearFlag && (Date.now() - parseInt(clearFlag, 10)) < 5000;
			
			if (shouldClearAll) {
				console.log('🧹 clearStoredDocumentsForTypes: Detected fresh flow start, clearing ALL documents');
				// Clear the flag
				localStorage.removeItem('_pgg_clear_all_docs_flag');
				
				// Clear ALL supporting documents
				const allKeys = await this.indexedDb.data_store.toCollection().keys();
				for (const key of allKeys) {
					if (typeof key === 'string' && key.startsWith('supporting_doc_')) {
						try {
							await this.indexedDb.data_store.delete(key);
							localStorage.removeItem(key);
							console.log(`🧹 Cleared all documents on fresh start: ${key}`);
						} catch (error) {
							console.error(`Error clearing document ${key}:`, error);
						}
					}
				}
				return; // Exit early - we've cleared everything
			}
			
			// Normal behavior: only clear documents that don't match current types
			// This is for navigating between nodes within the same flow
			const allKeys = await this.indexedDb.data_store.toCollection().keys();
			
			for (const key of allKeys) {
				if (typeof key === 'string' && key.startsWith('supporting_doc_')) {
					// Extract document type from key (format: 'supporting_doc_<type>')
					const docType = key.replace('supporting_doc_', '');
					
					// If this document type is not in the current types, clear it
					if (!currentTypes.includes(docType)) {
						try {
							// Remove from IndexedDB
							await this.indexedDb.data_store.delete(key);
							// Also remove from localStorage for backward compatibility
							localStorage.removeItem(key);
							console.log(`Cleared stored document for type: ${docType} (not in current types)`);
						} catch (error) {
							console.error(`Error clearing document ${key}:`, error);
						}
					}
				}
			}
		} catch (error) {
			console.error('Error clearing stored documents:', error);
		}
	}

	private async loadStoredDocuments(): Promise<void> {
		this.isLoading.set(true);

		try {
			const cards = this.uploadCards();
			// Get current document types to verify stored documents match
			const currentDocTypes = cards.map(c => c.document.type);
			
			for (let i = 0; i < cards.length; i++) {
				const card = cards[i];
				const key = `supporting_doc_${card.document.type}`;

				// Only load if this document type is in the current types
				if (!currentDocTypes.includes(card.document.type)) {
					continue;
				}

				try {
					// Try IndexedDB first (preferred)
					const indexedDoc = await this.indexedDb.data_store.get(key);
					if (indexedDoc?.data) {
						const doc = indexedDoc.data;
						// Verify the stored document type matches the current card type
						if (doc.documentType !== card.document.type) {
							console.log(`Skipping stored document - type mismatch: stored=${doc.documentType}, expected=${card.document.type}`);
							// Clear the mismatched document
							await this.indexedDb.data_store.delete(key);
							localStorage.removeItem(key);
							continue;
						}
						
						// Create a dummy File object to indicate a file is present
						// We can't restore the actual File object, but we can create a placeholder
						const fileName = doc.file?.name || doc.fileName || 'Stored file';
						const fileType = doc.file?.type || (fileName.endsWith('.pdf') ? 'application/pdf' : 'image/jpeg');
						
						// Create a Blob from base64 and convert to File-like object
						let base64Data = doc.base64;
						if (base64Data && base64Data.startsWith('data:')) {
							base64Data = base64Data.replace(/^data:.*?;base64,/, '');
						}
						
						if (!base64Data) {
							console.log(`Skipping stored document - no base64 data for ${key}`);
							continue;
						}
						
						const byteCharacters = atob(base64Data);
						const byteNumbers = new Array(byteCharacters.length);
						for (let j = 0; j < byteCharacters.length; j++) {
							byteNumbers[j] = byteCharacters.charCodeAt(j);
						}
						const byteArray = new Uint8Array(byteNumbers);
						const blob = new Blob([byteArray], { type: fileType });
						
						// Create a File object from the blob
						const file = new File([blob], fileName, { type: fileType });
						
						cards[i] = {
							...cards[i],
							file: file,
							fileName: fileName,
							base64: doc.base64,
							thumbnail: doc.thumbnail || null
						};
						continue;
					}

					// Try localStorage as fallback
					const stored = localStorage.getItem(key);
					if (stored) {
						const doc = JSON.parse(stored);
						// Verify the stored document type matches
						if (doc.documentType !== card.document.type) {
							console.log(`Skipping stored document from localStorage - type mismatch: stored=${doc.documentType}, expected=${card.document.type}`);
							localStorage.removeItem(key);
							continue;
						}
						
						const fileName = doc.file?.name || 'Stored file';
						const fileType = doc.file?.type || (fileName.endsWith('.pdf') ? 'application/pdf' : 'image/jpeg');
						
						// Create File object from stored base64
						let base64Data = doc.base64;
						if (base64Data && base64Data.startsWith('data:')) {
							base64Data = base64Data.replace(/^data:.*?;base64,/, '');
						}
						
						if (!base64Data) {
							console.log(`Skipping stored document from localStorage - no base64 data for ${key}`);
							continue;
						}
						
						const byteCharacters = atob(base64Data);
						const byteNumbers = new Array(byteCharacters.length);
						for (let j = 0; j < byteCharacters.length; j++) {
							byteNumbers[j] = byteCharacters.charCodeAt(j);
						}
						const byteArray = new Uint8Array(byteNumbers);
						const blob = new Blob([byteArray], { type: fileType });
						const file = new File([blob], fileName, { type: fileType });
						
						cards[i] = {
							...cards[i],
							file: file,
							fileName: fileName,
							base64: doc.base64,
							thumbnail: doc.thumbnail || null
						};
					}
				} catch (error) {
					console.error(`Error loading stored document ${key}:`, error);
				}
			}

			this.uploadCards.set([...cards]);
			
			// Sync loaded documents to bigForm for serverCall access
			this.syncUploadedDocsToBigForm();
		} catch (error) {
			console.error('Error loading stored documents:', error);
		} finally {
			this.isLoading.set(false);
			this.cdr.markForCheck();
		}
	}

	onFileSelected(event: Event, cardIndex: number): void {
		const input = event.target as HTMLInputElement;
		if (input.files && input.files.length > 0) {
			this.handleFile(input.files[0], cardIndex);
		}
	}

	onDragOver(event: DragEvent, cardIndex: number): void {
		event.preventDefault();
		event.stopPropagation();
		const cards = this.uploadCards();
		cards[cardIndex].isDragOver = true;
		this.uploadCards.set([...cards]);
	}

	onDragLeave(event: DragEvent, cardIndex: number): void {
		event.preventDefault();
		event.stopPropagation();
		const cards = this.uploadCards();
		cards[cardIndex].isDragOver = false;
		this.uploadCards.set([...cards]);
	}

	onDrop(event: DragEvent, cardIndex: number): void {
		event.preventDefault();
		event.stopPropagation();
		const cards = this.uploadCards();
		cards[cardIndex].isDragOver = false;
		this.uploadCards.set([...cards]);

		if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
			this.handleFile(event.dataTransfer.files[0], cardIndex);
		}
	}

	private async handleFile(file: File, cardIndex: number): Promise<void> {
		console.log('[SUPPORTING DOC] handleFile: original file', {
			name: file.name,
			type: file.type,
			sizeBytes: file.size
		});

		const cards = this.uploadCards();
		const card = cards[cardIndex];
		const doc = card.document;

		// Validate file type
		if (doc.allowed_file_types && doc.allowed_file_types.length > 0) {
			const fileExtension = file.name.split('.').pop()?.toLowerCase();
			const isValidType = doc.allowed_file_types.some(type => {
				const normalizedType = type.toLowerCase();
				if (normalizedType === 'image') {
					return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension || '');
				}
				return normalizedType === fileExtension;
			});

			if (!isValidType) {
				alert(`Invalid file type. Allowed types: ${doc.allowed_file_types.join(', ')}`);
				return;
			}
		}

		// Validate file size
		if (doc.max_file_size_mb) {
			const maxSizeBytes = doc.max_file_size_mb * 1024 * 1024;
			if (file.size > maxSizeBytes) {
				alert(`File size exceeds maximum of ${doc.max_file_size_mb}MB`);
				return;
			}
		}

		// Optionally resize large images before thumbnail + base64 conversion
		const { file: processedFile } = await this.maybeResizeImage(file);
		console.log('[SUPPORTING DOC] handleFile: processed file', {
			name: processedFile.name,
			type: processedFile.type,
			sizeBytes: processedFile.size
		});

		// Generate thumbnail for (potentially resized) image files
		const thumbnail = await this.generateThumbnail(processedFile);

		try {
			// Convert processed file to base64 (data URL -> raw base64)
			const dataUrl = await this.fileToDataUrl(processedFile);
			const base64 = dataUrl.replace(/^data:.*?;base64,/, '');
			console.log('[SUPPORTING DOC] handleFile: base64 length', base64.length);

			// Update card
			cards[cardIndex] = {
				...cards[cardIndex],
				file: processedFile,
				base64: base64,
				fileName: processedFile.name,
				thumbnail: thumbnail
			};
			this.uploadCards.set([...cards]);

			// Store locally
			await this.storeDocumentLocally({
				id: `${doc.type}-${Date.now()}`,
				documentType: doc.type,
				documentLabel: doc.label,
				file: { name: processedFile.name, type: processedFile.type, size: processedFile.size },
				fileName: processedFile.name,
				base64: base64,
				thumbnail: thumbnail,
				timestamp: Date.now()
			});

			// Sync to bigForm for serverCall access
			this.syncUploadedDocsToBigForm();

			this.cdr.markForCheck();
		} catch (error) {
			console.error('Error processing file:', error);
			alert('Error processing file. Please try again.');
		}
	}

	async removeFile(cardIndex: number): Promise<void> {
		const cards = this.uploadCards();
		const card = cards[cardIndex];

		if (card.document.type) {
			const key = `supporting_doc_${card.document.type}`;
			try {
				// Remove from localStorage (for backward compatibility)
				localStorage.removeItem(key);
				// Remove from IndexedDB - await to ensure it completes
				await this.indexedDb.data_store.delete(key);
			} catch (error) {
				console.error('Error removing stored document:', error);
				// Re-throw to ensure caller knows if deletion failed
				throw error;
			}
		}

		cards[cardIndex] = {
			...card,
			file: null,
			base64: null,
			fileName: null,
			thumbnail: null
		};
		this.uploadCards.set([...cards]);
		
		// Sync to bigForm for serverCall access
		this.syncUploadedDocsToBigForm();
		
		this.cdr.markForCheck();
	}

	private async generateThumbnail(file: File): Promise<string | null> {
		const fileExtension = file.name.split('.').pop()?.toLowerCase();
		const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension || '');
		const isPdf = fileExtension === 'pdf';

		// Generate thumbnail for image files
		if (isImage) {
			return new Promise((resolve) => {
				const reader = new FileReader();
				reader.onload = (e) => {
					const img = new Image();
					img.onload = () => {
						// Create canvas for thumbnail (max 100x100px)
						const canvas = document.createElement('canvas');
						const ctx = canvas.getContext('2d');
						if (!ctx) {
							resolve(null);
							return;
						}

						const maxSize = 100;
						let width = img.width;
						let height = img.height;

						// Calculate thumbnail dimensions maintaining aspect ratio
						if (width > height) {
							if (width > maxSize) {
								height = (height * maxSize) / width;
								width = maxSize;
							}
						} else {
							if (height > maxSize) {
								width = (width * maxSize) / height;
								height = maxSize;
							}
						}

						canvas.width = width;
						canvas.height = height;
						ctx.drawImage(img, 0, 0, width, height);

						// Convert to base64
						const thumbnail = canvas.toDataURL('image/jpeg', 0.7);
						resolve(thumbnail);
					};
					img.onerror = () => resolve(null);
					img.src = e.target?.result as string;
				};
				reader.onerror = () => resolve(null);
				reader.readAsDataURL(file);
			});
		}

		// Generate thumbnail for PDF files
		if (isPdf) {
			try {
				// Dynamic import of pdfjs-dist to avoid bundle size if not needed
				// NOTE: depending on bundler/interop, this may come back as a module namespace wrapper.
				// Normalize to the actual PDF.js API object (has getDocument + GlobalWorkerOptions).
				const pdfjsLib = this.normalizePdfJsImport(await import('pdfjs-dist'));
				
				// Set worker path - use CDN with version 3.11.174 (stable version)
				const workerOptions = pdfjsLib?.GlobalWorkerOptions;
				if (workerOptions && !workerOptions.workerSrc) {
					// Prefer a locally hosted worker shipped with the app (avoids CSP / CDN availability issues in prod,
					// and avoids relying on bundler-specific worker URL resolution).
					// Use document.baseURI so it works when app is deployed under a sub-path.
					try {
						workerOptions.workerSrc = new URL('assets/pdfjs/pdf.worker.min.js', document.baseURI).toString();
					} catch {
						workerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
					}
				}

				// Read file as array buffer
				const arrayBuffer = await file.arrayBuffer();
				const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
				const pdf = await loadingTask.promise;

				// Get first page
				const page = await pdf.getPage(1);
				const viewport = page.getViewport({ scale: 1.0 });

				// Create canvas for thumbnail (max 100x100px)
				const canvas = document.createElement('canvas');
				const ctx = canvas.getContext('2d');
				if (!ctx) {
					return null;
				}

				const maxSize = 100;
				const scale = maxSize / Math.max(viewport.width, viewport.height);
				const scaledViewport = page.getViewport({ scale: scale });

				canvas.width = scaledViewport.width;
				canvas.height = scaledViewport.height;

				const renderContext = {
					canvasContext: ctx,
					viewport: scaledViewport
				};

				// Type assertion needed due to pdfjs-dist type definitions
				await page.render(renderContext as never).promise;

				// Convert to base64
				const thumbnail = canvas.toDataURL('image/jpeg', 0.7);
				return thumbnail;
			} catch (error) {
				console.error('Error generating PDF thumbnail:', error);
				return null;
			}
		}

		// No thumbnail for other file types
		return null;
	}

	/**
	 * pdfjs-dist can be loaded as ESM/CJS and bundlers may wrap exports under `default`.
	 * We normalize the dynamic import so downstream usage is stable.
	 */
	private normalizePdfJsImport(mod: any): any {
		if (mod?.getDocument && mod?.GlobalWorkerOptions) return mod;
		if (mod?.default?.getDocument && mod?.default?.GlobalWorkerOptions) return mod.default;
		if (mod?.pdfjsLib?.getDocument && mod?.pdfjsLib?.GlobalWorkerOptions) return mod.pdfjsLib;
		return mod;
	}

	private async storeDocumentLocally(doc: {
		id: string;
		documentType: string;
		documentLabel: string;
		file: { name: string; type: string; size: number };
		fileName: string;
		base64: string;
		thumbnail: string | null;
		timestamp: number;
	}): Promise<void> {
		const key = `supporting_doc_${doc.documentType}`;
		try {
			// Always use IndexedDB for document storage - it has much larger capacity than localStorage
			await this.indexedDb.data_store.put({
				id: key,
				data: doc
			});
		} catch (error) {
			console.error('Error storing document locally:', error);
			// IndexedDB errors are typically fatal (quota exceeded, database issues, etc.)
			// No fallback needed as localStorage would also fail for large files
			throw error;
		}
	}

	private isImageFile(file: File): boolean {
		const fileExtension = file.name.split('.').pop()?.toLowerCase();
		return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension || '');
	}

	private async maybeResizeImage(file: File): Promise<{ file: File }> {
		// Only attempt resize for image types
		if (!this.isImageFile(file)) {
			console.log('[SUPPORTING DOC] maybeResizeImage: not an image, skipping', file.name);
			return { file };
		}

		// If already under threshold, keep as-is
		if (file.size <= MAX_IMAGE_BYTES) {
			console.log('[SUPPORTING DOC] maybeResizeImage: below threshold, skipping', {
				name: file.name,
				sizeBytes: file.size,
				maxBytes: MAX_IMAGE_BYTES
			});
			return { file };
		}

		console.log('[SUPPORTING DOC] maybeResizeImage: resizing needed', {
			name: file.name,
			sizeBytes: file.size,
			maxBytes: MAX_IMAGE_BYTES
		});

		// Read original file into a data URL
		const originalDataUrl = await this.fileToDataUrl(file);

		// Resize and compress until under threshold
		const resizedResult = await this.resizeDataUrl(originalDataUrl);
		if (!resizedResult) {
			// Fallback: if resize failed for any reason, keep original
			console.warn('[SUPPORTING DOC] maybeResizeImage: resize failed, using original', file.name);
			return { file };
		}
		const {
			dataUrl: resizedDataUrl,
			width: resizedWidth,
			height: resizedHeight,
			originalWidth,
			originalHeight
		} = resizedResult;

		const resizedFile = this.dataUrlToFile(resizedDataUrl, file.name);
		console.log('[SUPPORTING DOC] maybeResizeImage: resized result', {
			name: resizedFile.name,
			type: resizedFile.type,
			sizeBytes: resizedFile.size
		});

		if (this.ENABLE_IMAGE_RESIZE_DEBUG) {
			this.debugResizeVisible = true;
			this.debugResizeOriginal = originalDataUrl;
			this.debugResizeResized = resizedDataUrl;
			this.debugResizeFileName = file.name;
			this.debugResizeOriginalBytes = file.size;
			this.debugResizeResizedBytes = resizedFile.size;
			this.debugResizeOriginalWidth = originalWidth;
			this.debugResizeOriginalHeight = originalHeight;
			this.debugResizeResizedWidth = resizedWidth;
			this.debugResizeResizedHeight = resizedHeight;
			this.cdr.markForCheck();
		}

		return { file: resizedFile };
	}

	private async fileToDataUrl(file: File): Promise<string> {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = e => resolve(e.target?.result as string);
			reader.onerror = () => reject(new Error('Error reading file'));
			reader.readAsDataURL(file);
		});
	}

	private async resizeDataUrl(
		dataUrl: string
	): Promise<{
		dataUrl: string;
		width: number;
		height: number;
		originalWidth: number;
		originalHeight: number;
	} | null> {
		const img = await this.loadImage(dataUrl);
		console.log('[SUPPORTING DOC] resizeDataUrl: original dimensions', {
			width: img.width,
			height: img.height
		});

		const originalWidth = img.width;
		const originalHeight = img.height;

		let width = originalWidth;
		let height = originalHeight;

		// Scale down to fit within MAX_IMAGE_DIMENSION, preserving aspect ratio
		const maxDim = MAX_IMAGE_DIMENSION;
		if (width > maxDim || height > maxDim) {
			const ratio = Math.min(maxDim / width, maxDim / height);
			width = Math.round(width * ratio);
			height = Math.round(height * ratio);
		}

		const canvas = document.createElement('canvas');
		canvas.width = width;
		canvas.height = height;
		const ctx = canvas.getContext('2d');
		if (!ctx) {
			return null;
		}
		ctx.drawImage(img, 0, 0, width, height);

		// Iteratively reduce JPEG quality until under byte threshold or minimum quality
		let quality = 0.8;
		let resizedDataUrl = canvas.toDataURL('image/jpeg', quality);

		const approxBytes = (url: string): number => {
			const base64 = url.replace(/^data:.*?;base64,/, '');
			return Math.ceil((base64.length * 3) / 4);
		};

		let bytes = approxBytes(resizedDataUrl);
		console.log('[SUPPORTING DOC] resizeDataUrl: initial encode', {
			quality,
			bytes,
			maxBytes: MAX_IMAGE_BYTES
		});

		while (bytes > MAX_IMAGE_BYTES && quality > 0.3) {
			quality -= 0.1;
			resizedDataUrl = canvas.toDataURL('image/jpeg', quality);
			bytes = approxBytes(resizedDataUrl);
			console.log('[SUPPORTING DOC] resizeDataUrl: re-encode', { quality, bytes });
		}

		console.log('[SUPPORTING DOC] resizeDataUrl: final', {
			quality,
			bytes,
			width,
			height
		});
		return {
			dataUrl: resizedDataUrl,
			width,
			height,
			originalWidth,
			originalHeight
		};
	}

	private loadImage(src: string): Promise<HTMLImageElement> {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.onload = () => resolve(img);
			img.onerror = () => reject(new Error('Error loading image'));
			img.src = src;
		});
	}

	private dataUrlToFile(dataUrl: string, fileName: string): File {
		const arr = dataUrl.split(',');
		const mimeMatch = arr[0].match(/:(.*?);/);
		const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
		const bstr = atob(arr[1]);
		const n = bstr.length;
		const u8arr = new Uint8Array(n);
		for (let i = 0; i < n; i++) {
			u8arr[i] = bstr.charCodeAt(i);
		}
		return new File([u8arr], fileName, { type: mime });
	}

	closeDebugResize(): void {
		this.debugResizeVisible = false;
		this.debugResizeOriginal = null;
		this.debugResizeResized = null;
		this.debugResizeFileName = null;
		this.debugResizeOriginalBytes = null;
		this.debugResizeResizedBytes = null;
		this.debugResizeOriginalWidth = null;
		this.debugResizeOriginalHeight = null;
		this.debugResizeResizedWidth = null;
		this.debugResizeResizedHeight = null;
		this.debugOriginalZoom = 1.4;
		this.debugOriginalOffsetX = 0;
		this.debugOriginalOffsetY = 0;
		this.debugResizedZoom = 1.4;
		this.debugResizedOffsetX = 0;
		this.debugResizedOffsetY = 0;
	}

	getDebugTransform(type: 'original' | 'resized'): string {
		const zoom = type === 'original' ? this.debugOriginalZoom : this.debugResizedZoom;
		const offsetX = type === 'original' ? this.debugOriginalOffsetX : this.debugResizedOffsetX;
		const offsetY = type === 'original' ? this.debugOriginalOffsetY : this.debugResizedOffsetY;
		return `translate(${offsetX}px, ${offsetY}px) scale(${zoom})`;
	}

	setDebugZoom(type: 'original' | 'resized', zoom: number | string): void {
		const parsed = typeof zoom === 'string' ? parseFloat(zoom) : zoom;
		if (Number.isNaN(parsed)) {
			return;
		}
		const minZoom = 1;
		const maxZoom = 4;
		const clamped = Math.min(maxZoom, Math.max(minZoom, parsed));
		if (type === 'original') {
			this.debugOriginalZoom = clamped;
		} else {
			this.debugResizedZoom = clamped;
		}
	}

	nudgeDebugOffset(type: 'original' | 'resized', dx: number, dy: number): void {
		if (type === 'original') {
			this.debugOriginalOffsetX += dx;
			this.debugOriginalOffsetY += dy;
		} else {
			this.debugResizedOffsetX += dx;
			this.debugResizedOffsetY += dy;
		}
	}

	formatBytes(size: number | null): string {
		if (size === null || size === undefined) {
			return '';
		}

		const kb = size / 1024;
		if (kb >= 1024) {
			const mb = kb / 1024;
			return `${mb.toFixed(2)} MB`;
		}

		return `${kb.toFixed(1)} kB`;
	}

	openFilePicker(cardIndex: number): void {
		const fileInputsArray = this.fileInputs.toArray();
		if (fileInputsArray[cardIndex]) {
			fileInputsArray[cardIndex].nativeElement.click();
		}
	}

	getAcceptString(doc: DocumentRequirement): string {
		if (!doc.allowed_file_types || doc.allowed_file_types.length === 0) {
			return '*/*';
		}

		return doc.allowed_file_types
			.map(type => {
				const normalizedType = type.toLowerCase();
				if (normalizedType === 'image') {
					return 'image/*';
				}
				if (normalizedType === 'pdf') {
					return 'application/pdf';
				}
				return `.${normalizedType}`;
			})
			.join(',');
	}

	hasAllRequiredDocuments(): boolean {
		return this.allRequiredUploaded();
	}
}

